import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatLabel, speakerLabel } from './importCitation';
import './SourceViewer.css';

/**
 * Read-only imported document. Jump + highlight the cited chunk.
 * One Back. Not an editor. Not original-formatting fidelity.
 */
function SourceViewer({ source, error, onBack }) {
  const citedRef = useRef(null);

  useEffect(() => {
    if (!citedRef.current) return undefined;
    citedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return undefined;
  }, [source?.cited_chunk_id]);

  const chunks = source?.chunks || [];
  const isTranscript = source?.declared_format === 'transcript';

  return createPortal(
    <div className="import-source__backdrop" data-testid="import-source-viewer">
      <div
        className="import-source"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-source-title"
      >
        <div className="import-source__header">
          <p className="import-source__eyebrow">Imported source</p>
          <h2 id="import-source-title">
            {source?.original_filename || 'Imported document'}
          </h2>
          {source?.declared_format && (
            <p className="import-source__format">{formatLabel(source.declared_format)}</p>
          )}
          <button
            type="button"
            className="import-source__back"
            onClick={onBack}
          >
            Back
          </button>
        </div>

        <div className="import-source__body">
          {error && (
            <p className="import-source__error" role="alert">{error}</p>
          )}
          {chunks.map((chunk) => {
            const cited = chunk.id === source.cited_chunk_id;
            const role = speakerLabel(chunk.speaker_role);
            return (
              <section
                key={chunk.id}
                id={`import-chunk-${chunk.id}`}
                ref={cited ? citedRef : undefined}
                className={
                  cited
                    ? 'import-source__chunk import-source__chunk--cited'
                    : 'import-source__chunk'
                }
                data-cited={cited ? 'true' : 'false'}
              >
                {isTranscript && role && (
                  <span className="import-source__speaker">{role}</span>
                )}
                <p className="import-source__text">{chunk.content}</p>
              </section>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SourceViewer;
